import { AccordionPanel } from './AccordionPanel';
import { events } from '../analytics/events';
import { Order, orderByOptions } from '../models/widget-state';
import { useTrackedHandler } from '../hooks/useTracking';

import {
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import capitalize from 'lodash-es/capitalize';
import React from 'react';
import styled from 'styled-components';
import SwapVertIcon from '@mui/icons-material/SwapVert';

export interface SortComponentProps {
  ordering: Order | null;
  onOrderChanged?: (order: Order | null) => void;
  isExpanded: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const relevanceKey = 'relevance' as const;
const selectionOptions = [relevanceKey, ...orderByOptions];
type SelectionOptions = (typeof selectionOptions)[number];

export function SortComponent(props: SortComponentProps): JSX.Element {
  const {
    ordering,
    onOrderChanged = () => {},
    isExpanded,
    onExpandedChange = () => {},
  } = props;

  const handleSelectedOrderChange = useTrackedHandler(
    events.sort.SortByChanged,
    (_: React.ChangeEvent<unknown>, sortBy: string): void => {
      const by: SelectionOptions = sortBy as SelectionOptions;
      if (by === relevanceKey) {
        onOrderChanged(null);
      } else if (by === 'year' || by === 'citation') {
        onOrderChanged({ by, isDescending: true });
      } else {
        onOrderChanged({ by, isDescending: false });
      }
    },
    (_, sortBy) => ({ sortBy }),
  );

  const toggleCurrentDirection = useTrackedHandler(
    events.sort.DirectionToggleClicked,
    (): void => {
      if (ordering !== null) {
        const isDescending = !ordering.isDescending;
        onOrderChanged({ ...ordering, isDescending });
      }
    },
    () => ({ isDescending: !ordering?.isDescending }),
  );

  const reverseButton = (
    <StyledIconButton onClick={toggleCurrentDirection}>
      Reverse
      <SwapVertIcon />
    </StyledIconButton>
  );

  const selectedOrder = ordering?.by ?? relevanceKey;

  let title = 'Sort by';
  if (selectedOrder !== relevanceKey) {
    title += ` (${selectedOrder})`;
  }
  return (
    <AccordionPanel
      title={title}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <FormControl>
        <RadioGroup value={selectedOrder} onChange={handleSelectedOrderChange}>
          {selectionOptions.map((option) => {
            const labelText =
              option === 'citation' ? 'Citations' : capitalize(option);
            const label = (
              <LabelDiv>
                <span>{labelText}</span>
                {ordering?.by && ordering.by === option && reverseButton}
              </LabelDiv>
            );

            return (
              <StyledFormControlLabel
                key={option}
                value={option}
                label={label}
                control={<Radio />}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </AccordionPanel>
  );
}

const LabelDiv = styled.div`
  width: 100%;
  display: flex;
  direction: row;
  justify-content: space-between;
  align-items: center;
  user-select: none;
`;

const StyledIconButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: rgb(250, 242, 233);
    padding: 4px 8px;
  }

  & .MuiSvgIcon-root {
    width: 16px;
    height: 16px;
    margin-left: 4px;
    margin-top: 2px;
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  & .MuiFormControlLabel-label {
    font-size: ${({ theme }) => theme.font.size.small};
    width: 100%;
  }
`;
